











































import Vue from 'vue';
import axios from 'axios';
import CookieLaw from 'vue-cookie-law';
import { get } from 'lodash';
import { isEmail } from 'validator';
import CookieBanner from '@/components/CookieBanner.vue';
import EventBus from '@/eventBus';
import VueIntercom from 'vue-intercom';
import setCustomColors from './customColors';
import CoAdminAlert from './components/Organisms/co-admin-alert/CoAdminAlert.vue';
import CoToastStack from './components/Organisms/co-toast-stack/CoToastStack.vue';
import CoTopNavBarMobile from './components/Organisms/co-top-nav-bar-mobile/CoTopNavBarMobile.vue';
import { applyUserLocale } from './utils/locale.js';
import { clearOnlineUsers, replaceOnlineUsers, setUserPresence } from './utils/realtimePresence';
import { readServerSentEvents } from './utils/serverSentEvents.ts';

Vue.use(VueIntercom, { appId: 'kttwojyk' });

type IntercomEvent = {
    type: 'update' | 'trackEvent';
    payload: any;
    eventName?: string;
};

const CUSTOM_MENU_LABELS_OVERRIDE_FLAG = 'frontend.custom-menu-labels-override';

const parseCustomMenuLabelOverrides = (variant) => {
    if (!variant?.enabled || typeof variant?.payload?.value !== 'string') {
        return {};
    }

    try {
        const parsedPayload = JSON.parse(variant.payload.value);
        if (!parsedPayload || typeof parsedPayload !== 'object' || Array.isArray(parsedPayload)) {
            return {};
        }
        return {
            feed: typeof parsedPayload.feed === 'string' ? parsedPayload.feed : '',
            community: typeof parsedPayload.community === 'string' ? parsedPayload.community : '',
        };
    } catch {
        return {};
    }
};

// Keep the legacy chat event shape so existing messenger views and toast handlers do not need to change.
const normalizeRealtimeChatMessage = (event) => {
    const chatMessage = event?.message;
    if (!chatMessage?.thread_id) {
        return null;
    }

    return {
        id: chatMessage.message_id,
        message_id: chatMessage.message_id,
        thread_id: chatMessage.thread_id,
        space_id: chatMessage.space_id,
        from: chatMessage.from_user_id,
        from_user_id: chatMessage.from_user_id,
        to: chatMessage.to_user_id,
        to_user_id: chatMessage.to_user_id,
        text: chatMessage.text,
        text_json: chatMessage.text_json,
        timestamp: chatMessage.timestamp,
        ts: event?.ts || chatMessage.timestamp,
    };
};

export default {
    name: 'App',
    components: {
        CookieBanner,
        CookieLaw,
        CoAdminAlert,
        CoToastStack,
        CoTopNavBarMobile,
    },
    data() {
        return {
            me: null,
            appVisible: false,
            defaultColors: [
                {
                    name: 'purple',
                    value: '#5100ff',
                },
                {
                    name: 'red',
                    value: '#fd6969',
                },
                {
                    name: 'text',
                    value: '#212529',
                },
            ],
            intercomHasBooted: false,
            intercomBootPromise: null,
            // Realtime presence is app-wide so it also runs for manage/admin routes.
            realtimeStreamController: null,
            realtimeStreamReconnectTimeout: null,
            realtimeStreamHasConnected: false,
            realtimeStreamRecoveryPending: false,
            realtimeStreamRecoveryInProgress: false,
            tabAttentionInterval: null as number | null,
            tabAttentionBaseTitle: '',
            tabAttentionDotVisible: false,
        };
    },
    async beforeCreate() {
        if (this.$route.query.m === 'mobile') {
            this.$store.commit('SET_IS_IN_MOBILE', true);
        } else if (this.$route.query.m === 'desktop') {
            this.$store.commit('SET_IS_IN_MOBILE', false);
        }

        if (this.$route.query.appversion) {
            this.$store.commit('SET_MOBILE_APP_VERSION', this.$route.query.appversion);
        }

        if (
            this.$store &&
            this.$store.state &&
            this.$store.state.space &&
            !this.$router.currentRoute.path.startsWith('/admin')
        ) {
            const colorData = [
                {
                    name: 'primary',
                    value: this.$store.state.space.PrimaryColor,
                },
                {
                    name: 'red',
                    value: this.$store.state.space.SecondaryColor,
                },
                {
                    name: 'text',
                    value: this.$store.state.space.TextColor,
                },
            ];
            setCustomColors(colorData);
        }

        await this.$store
            .dispatch('getSpaceSettings', null)
            .then((response) => {
                this.setFavicon();
                if (
                    this.$store &&
                    this.$store.state &&
                    this.$store.state.space &&
                    !this.$router.currentRoute.path.startsWith('/admin')
                ) {
                    const colorData = [
                        {
                            name: 'primary',
                            value: this.$store.state.space.PrimaryColor,
                        },
                        {
                            name: 'red',
                            value: this.$store.state.space.SecondaryColor,
                        },
                        {
                            name: 'text',
                            value: this.$store.state.space.TextColor,
                        },
                    ];
                    setCustomColors(colorData);
                }
                // update unleash context and start
                this.$unleash.updateContext({
                    userId: this.$store.state.me.ID,
                    remoteAddress: this.$store.state.space.ID, // instead of remoteAddress we use spaceId
                    properties: {
                        spaceID: this.$store.state.space.ID,
                        enableAfter: new Date((this.$store.state.space?.CreatedAt || 0) * 1000).toISOString(), // this is needed to target users with feature flags based on their signup date
                    },
                });
                // Check if feature flaging system is available
                this.$unleash.on('ready', () => {
                    this.cacheCustomMenuLabelOverrides();
                    if (this.$unleash.isEnabled('frontend.always-on')) {
                        console.info('frontend.always-on is enabled');
                    } else {
                        console.info('frontend.always-on is disabled');
                    }

                    if (this.$unleash.isEnabled('circles.frontend')) {
                        this.$store.commit('SET_CIRCLES_ON', true);
                    } else {
                        this.$store.commit('SET_CIRCLES_ON', false);
                    }
                });
                // Keep persisted custom menu label overrides in sync with Unleash polling updates.
                this.$unleash.on('update', this.cacheCustomMenuLabelOverrides);

                // Start the background polling
                this.$unleash.start();
                // update app visible
                this.appVisible = true;
            })
            .catch((error) => {
                this.appVisible = true;
            })
            .finally(() => {
                this.appVisible = true;
            });
        // Preload legal agreements once so checkout and signup flows can reuse them.
        await this.$store.dispatch('getLegalAgreements');
    },
    created() {
        const initialTitle = this.$store.state?.space?.Name;
        if (initialTitle) document.title = initialTitle;
        this.getCommunityLimits();
        // Intercom initialization for the session if script is ready
        // if not it will be initiated by the the this.$intercom.ready watcher
        if (this.$intercom && this.$intercom.ready && !this.intercomHasBooted) {
            this.bootIntercom();
        }
        // intercom event handlers
        EventBus.$on('intercomUpdate', (data: IntercomEvent) => {
            this.processIntercomMessage({
                type: 'update',
                ...data,
            });
        });
        EventBus.$on('intercomTrackEvent', (data: IntercomEvent) => {
            this.processIntercomMessage({
                type: 'trackEvent',
                ...data,
            });
        });

        if (this.$route && this.$route.query && this.$route.query.intercomevent) {
            this.processIntercomMessage({
                type: 'trackEvent',
                eventName: this.$route.query.intercomevent,
                payload: {
                    info: "event from url query param 'intercomevent'",
                },
            });
        }
        // add force show intercom global method to Vue prototype to be used in other components
        // it overrides the launchers introduced by custom javascript by shutting down any existing session and booting a new one with the default launcher shown
        Vue.prototype.$forceShowCoappIntercom = async () => {
            if (this.$intercom && typeof this.$intercom.shutdown === 'function') {
                this.$intercom.shutdown(); // clear any session started by customer JS
            }
            this.intercomHasBooted = false; // reset booted flag
            try {
                await this.bootIntercom();
            } catch {
                // Keep support launcher clicks from breaking the app if Intercom cannot boot.
            }
            if (this.intercomHasBooted && this.$intercom && typeof this.$intercom.show === 'function') {
                this.$intercom.show();
            }
        };
    },
    computed: {
        // Read cached legal agreements from the store to avoid per-view fetches.
        privacyPolicy() {
            return this.$store.state.privacyPolicy;
        },
        showUpgradeBar() {
            return this.$store?.state?.space?.PlanSlug === 'free';
        },
        isSaasRoute() {
            const match = get(window, 'location.hostname', '').match(/^app|staging-app\.(coapp\.io|localhost)/gi);
            return !!(match && match.length > 0);
        },
        SupportURL() {
            if (
                this.$store &&
                this.$store.state &&
                this.$store.state.space &&
                typeof this.$store.state.space.SupportURL === 'string'
            ) {
                let url = this.$store.state.space.SupportURL.trim();
                if (isEmail(url)) {
                    url = `mailto:${url}`;
                } else {
                    url = url.startsWith('http') ? url : `https://${url}`;
                }
                return url.trim();
            } else {
                return null;
            }
        },
        isAdmin() {
            if (
                this.$store.state.me &&
                this.$store.state.me.Permissions &&
                this.$store.state.me.Permissions.includes('space_admin')
            ) {
                return true;
            }
            return false;
        },
    },
    mounted() {
        this.injectCustomerJavaScript();

        if (this.isLocalStorage()) {
            this.$store.commit('LOCALSTORAGE_TRUE');
        }
        this.getPaymentState();

        document.addEventListener('visibilitychange', this.handleRealtimeStreamResumeTrigger);
        window.addEventListener('pageshow', this.handleRealtimeStreamResumeTrigger);
        window.addEventListener('online', this.handleRealtimeStreamResumeTrigger);
        window.addEventListener('offline', this.handleRealtimeStreamResumeTrigger);
        document.addEventListener('visibilitychange', this.clearTabAttentionWhenVisible);
        EventBus.$on('TAB_ATTENTION', this.handleTabAttention);
        this.initRealtimeStream();
    },
    beforeDestroy() {
        // close intercom session
        this.$intercom.shutdown();
        document.removeEventListener('visibilitychange', this.handleRealtimeStreamResumeTrigger);
        window.removeEventListener('pageshow', this.handleRealtimeStreamResumeTrigger);
        window.removeEventListener('online', this.handleRealtimeStreamResumeTrigger);
        window.removeEventListener('offline', this.handleRealtimeStreamResumeTrigger);
        document.removeEventListener('visibilitychange', this.clearTabAttentionWhenVisible);
        EventBus.$off('TAB_ATTENTION', this.handleTabAttention);
        this.clearTabAttention();
        this.destroyRealtimeStream();
    },
    watch: {
        // Intercom initialization on script ready
        '$intercom.ready'(ready) {
            this.bootIntercom();
        },
        '$store.state.me.Language': {
            handler(language) {
                if (!language) {
                    return;
                }
                applyUserLocale(this, language);
            },
            immediate: true,
        },
        // Intercom initialization me ready
        '$store.state.me'(newVal) {
            this.bootIntercom();
            this.initRealtimeStream();
        },
        // Intercom initialization on space ready
        '$store.state.space'(newVal) {
            this.bootIntercom();
        },
        communityName(newVal) {
            if (newVal) {
                document.title = newVal;
            }
        },
    },
    methods: {
        getCommunityLimits() {
            //  if url is not app.coapp.io and staging-app.coapp.io and app.localhost:8080
            const domain = window.location.hostname;
            if (
                domain.includes('app.coapp.io') ||
                domain.includes('staging-app.coapp.io') ||
                domain.includes('app.localhost')
            ) {
                return;
            }
            this.$store.dispatch('getLimits');
        },
        cacheCustomMenuLabelOverrides() {
            if (!this.$unleash || !this.$store) {
                return;
            }

            // Persist only the custom menu labels variant payload used by navigation.
            this.$store.commit(
                'SET_CUSTOM_MENU_LABEL_OVERRIDES',
                parseCustomMenuLabelOverrides(this.$unleash.getVariant(CUSTOM_MENU_LABELS_OVERRIDE_FLAG))
            );
        },
        goBack() {
            // if no history exists go to home
            if (window.history.length > 1) {
                this.$router.go(-1);
            } else {
                this.$router.push('/');
            }
        },
        logOut() {
            this.destroyRealtimeStream();
            this.$store.dispatch('logout').finally(() => {
                this.$router.push('/login');
            });
        },
        setFavicon() {
            const faviconurl = get(this.$store, 'state.space.FaviconURL', null);
            if (faviconurl) {
                let link = document.querySelector("link[rel~='icon']");
                if (!link) {
                    link = document.createElement('link');
                    link['rel'] = 'icon';
                    document.head.appendChild(link);
                }
                // icon.value = this.$store.state.space.Favicon;
                link['href'] = faviconurl;
            } else {
                let link = document.querySelector("link[rel~='icon']");
                if (!link) {
                    link = document.createElement('link');
                    link['rel'] = 'icon';
                    document.head.appendChild(link);
                }
                link['href'] = '/favicon.ico';
            }
        },
        // isLocalStorage checks if local storage available to detect wheather it is CNA popup or not
        isLocalStorage() {
            const test = 'test';
            try {
                localStorage.setItem(test, test);
                localStorage.removeItem(test);
                return true;
                // available
            } catch (e) {
                return false;
                // unavailable
            }
        },

        // getPaymentState - returns payment service readyness state
        getPaymentState() {
            axios({
                method: 'GET',
                url: '/user/account/billing/payment-state',
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    if (response && response.data) {
                        this.$store.commit('SET_PAYMENT_READY_STATE', response.data.ChargesEnabled);
                    } else {
                        this.$store.commit('SET_PAYMENT_READY_STATE', false);
                    }
                })
                .catch((error) => {
                    this.$store.commit('SET_PAYMENT_READY_STATE', false);
                });
        },
        initRealtimeStream() {
            if (this.realtimeStreamController) return;

            if (!get(this, '$store.state.me.ID', null)) return;

            this.realtimeStreamController = new AbortController();
            this.readRealtimeStream(this.realtimeStreamController.signal);
        },
        async readRealtimeStream(signal) {
            try {
                await readServerSentEvents({
                    url: this.realtimeStreamURL(),
                    credentials: 'include',
                    headers: {
                        Accept: 'text/event-stream',
                    },
                    signal,
                    isHeartbeat: ({ data }) => {
                        try {
                            return JSON.parse(data)?.type === 'ping';
                        } catch {
                            return false;
                        }
                    },
                    onConnected: this.handleRealtimeStreamConnected,
                    onMessage: ({ data }) => {
                        this.handleRealtimeStreamMessage(data);
                    },
                });

                if (!signal.aborted) {
                    this.scheduleRealtimeStreamReconnect();
                }
            } catch {
                if (signal.aborted) return;

                this.scheduleRealtimeStreamReconnect();
            }
        },
        handleRealtimeStreamConnected() {
            if (this.realtimeStreamHasConnected) {
                // Views use this signal to recover messages missed while the shared stream was disconnected.
                EventBus.$emit('REALTIME_STREAM_RECONNECTED', { connectedAt: Date.now() });
            }
            this.realtimeStreamHasConnected = true;
        },
        handleRealtimeStreamResumeTrigger(event: Event & { persisted?: boolean }) {
            if (event.type === 'offline' || document.visibilityState === 'hidden') {
                this.realtimeStreamRecoveryPending = true;
                return;
            }
            if (event.type === 'pageshow' && !event.persisted) return;
            if (!this.realtimeStreamRecoveryPending && event.type !== 'online') return;
            if (this.realtimeStreamRecoveryInProgress || navigator.onLine === false) return;

            this.realtimeStreamRecoveryPending = false;
            this.realtimeStreamRecoveryInProgress = true;
            this.destroyRealtimeStream(false);

            this.$nextTick(() => {
                this.initRealtimeStream();
                this.realtimeStreamRecoveryInProgress = false;
            });
        },
        handleRealtimeStreamMessage(data) {
            if (!data) return;
            try {
                this.handleRealtimeMessage(JSON.parse(data));
            } catch {
                // Ignore malformed stream events and keep the connection alive.
            }
        },
        handleRealtimeMessage(message) {
            if (!message || message.type === 'ping') return;

            if (message.type === 'chat_message') {
                const normalizedMessage = normalizeRealtimeChatMessage(message);
                if (!normalizedMessage) return;

                this.$store.commit('HANDLE_REALTIME_CHAT_MESSAGE', normalizedMessage);
                if (
                    normalizedMessage.from_user_id &&
                    String(normalizedMessage.from_user_id) !== String(get(this, '$store.state.me.ID', ''))
                ) {
                    this.handleTabAttention();
                }
                return;
            }

            if (message.type === 'comment_create') {
                const comment = message.message;
                if (!comment?.PostID) return;

                EventBus.$emit(`COMMENTS_${comment.PostID}`, {
                    ...comment,
                    ts: message.ts,
                });
                return;
            }

            if (message.type === 'comment_modify') {
                const comment = message.message;
                const commentID = comment?.ID || comment?.CommentID;
                if (!commentID || typeof comment !== 'object' || Array.isArray(comment)) return;

                // Comment cards subscribe to their own topic so only the affected card rerenders.
                EventBus.$emit(`comment_modify_${commentID}`, {
                    ...comment,
                    ts: message.ts,
                });
                return;
            }

            if (message.type === 'post_create') {
                if (!message.message || typeof message.message !== 'object' || Array.isArray(message.message)) return;

                // Feed views consume post updates without opening another realtime connection.
                const post = {
                    ...message.message,
                    ts: message.ts,
                };
                EventBus.$emit('POST_CREATE', post);

                // Parent views subscribe to their own topic so unrelated pages and events do not process the post.
                if (post.ParentID && post.ParentType) {
                    EventBus.$emit(`POST_CREATE_${post.ParentType}_${post.ParentID}`, post);
                }
                return;
            }

            if (message.type === 'post_modify') {
                const post = message.message;
                const postID = post?.ID || post?.PostID;
                if (!postID || typeof post !== 'object' || Array.isArray(post)) return;

                // Post cards subscribe to their own topic so only the affected card rerenders.
                EventBus.$emit(`post_modify_${postID}`, {
                    ...post,
                    ts: message.ts,
                });
                return;
            }

            if (message.type === 'online_users') {
                this.handleOnlineUsersSnapshot(message.user_ids || [], message.ts);
                return;
            }

            if (message.type === 'user_online') {
                this.emitUserPresence(message.user_id, true, message.ts);
                return;
            }

            if (message.type === 'user_offline') {
                this.emitUserPresence(message.user_id, false, message.ts);
            }
        },
        handleTabAttention() {
            if (document.visibilityState !== 'hidden' || this.tabAttentionInterval !== null) return;

            // Blink a compact marker while preserving the community-provided browser title.
            this.tabAttentionBaseTitle = document.title;
            this.updateTabAttentionTitle();
            this.tabAttentionInterval = window.setInterval(this.updateTabAttentionTitle, 800);
        },
        updateTabAttentionTitle() {
            this.tabAttentionDotVisible = !this.tabAttentionDotVisible;
            document.title = this.tabAttentionDotVisible
                ? `● ${this.tabAttentionBaseTitle}`
                : this.tabAttentionBaseTitle;
        },
        clearTabAttentionWhenVisible() {
            if (document.visibilityState === 'visible') this.clearTabAttention();
        },
        clearTabAttention() {
            if (this.tabAttentionInterval !== null) {
                window.clearInterval(this.tabAttentionInterval);
                this.tabAttentionInterval = null;
            }
            if (this.tabAttentionBaseTitle) document.title = this.tabAttentionBaseTitle;
            this.tabAttentionBaseTitle = '';
            this.tabAttentionDotVisible = false;
        },
        handleOnlineUsersSnapshot(userIds, changedAt) {
            // The gateway snapshot is sent before registering this stream, so include the current user locally.
            const snapshotUserIds = [...userIds, get(this, '$store.state.me.ID', null)].filter(Boolean);
            const { onlineUserIds, offlineUserIds } = replaceOnlineUsers(snapshotUserIds);

            offlineUserIds.forEach((userId) => {
                this.emitUserPresence(userId, false, changedAt);
            });

            onlineUserIds.forEach((userId) => {
                this.emitUserPresence(userId, true, changedAt);
            });
        },
        emitUserPresence(userId, online, changedAt) {
            const normalizedUserId = setUserPresence(userId, online);
            if (!normalizedUserId) return;

            EventBus.$emit(`USER_PRESENCE_${normalizedUserId}`, {
                userId: normalizedUserId,
                online,
                changedAt,
            });
            EventBus.$emit('USER_PRESENCE_UPDATED', {
                userId: normalizedUserId,
                online,
                changedAt,
            });
        },
        realtimeStreamURL() {
            return new URL('/api/v1/realtime/stream', axios.defaults.baseURL || window.location.origin).toString();
        },
        scheduleRealtimeStreamReconnect() {
            this.destroyRealtimeStream(false);

            this.realtimeStreamReconnectTimeout = window.setTimeout(() => {
                this.realtimeStreamReconnectTimeout = null;
                this.initRealtimeStream();
            }, 3000);
        },
        destroyRealtimeStream(clearPresence = true) {
            if (this.realtimeStreamReconnectTimeout) {
                clearTimeout(this.realtimeStreamReconnectTimeout);
                this.realtimeStreamReconnectTimeout = null;
            }

            if (this.realtimeStreamController) {
                this.realtimeStreamController.abort();
                this.realtimeStreamController = null;
            }

            if (clearPresence) {
                this.realtimeStreamHasConnected = false;
                this.realtimeStreamRecoveryPending = false;
                this.realtimeStreamRecoveryInProgress = false;
                clearOnlineUsers().forEach((userId) => {
                    this.emitUserPresence(userId, false, null);
                });
            }
        },
        async bootIntercom() {
            // test if all credentials are available skip if not
            if (this.intercomBootPromise) {
                await this.intercomBootPromise;
                return;
            }
            if (
                this.intercomHasBooted ||
                !this.$intercom.ready ||
                !get(this.$store.state, 'space.ID', null) ||
                !get(this.$store.state, 'me.ID', null)
            ) {
                // Intercom credentials not ready, skipping boot attempt
                return;
            }
            // Intercom credentials are available ... booting intercom
            // 1. boot if user = admin
            if ((this.$store?.state?.me?.Permissions || '').includes('space_admin')) {
                // skip boot if the route contains /embed
                if (this.$route.path.includes('/embed')) {
                    return;
                }

                // Share the in-flight boot so ready/store watchers and support clicks do not boot twice.
                this.intercomBootPromise = (async () => {
                    try {
                        // fetch the signed JWT token for intercom from the backend to securely identify the user to intercom
                        const response = await axios.post('/user/intercom/token', null, { withCredentials: true });
                        const token = response?.data?.token || null;
                        if (!token) {
                            return;
                        }

                        const me = this.$store?.state?.me || {};
                        const intercomUserData = {
                            intercom_user_jwt: token, // the signed JWT token from the backend to securely identify the user to intercom, only needed for admins
                            name: me.Profile?.Name,
                        };
                        const space = this.$store?.state?.space || {};
                        const intercomCommunityData = {
                            spaceID: space.ID,
                            builtInDomain: space.BuiltInDomain, // this is the built in domain name of the space as it was configured on community creation
                            // info needed for intercom onboarding messages
                            'Community Name': space.Name,
                            'Community Created Date': space.CreatedAt,
                            'Community Domain': space.DomainName, // this is the actual domain name of the space if it was customized
                            'Community Plan Slug': space.PlanSlug, // the current plan of the community (e.g. "free", "starter", "professional")
                        };
                        // boot intercom with user data
                        // should not be booted elsewhere in the app
                        this.$intercom.boot({
                            ...intercomUserData,
                            ...intercomCommunityData,
                            hide_default_launcher: this.$isMobile || this.isInMobile, // hide if Vue.prototype.$isMobile flag is set
                        });
                        this.$intercom.hide(); // make sure messenger is closed on boot

                        // 2. check if intercom qeue in store has messages
                        // set the intercom ready flag IMPORTANT if this is not set there will be an infinite loop
                        this.intercomHasBooted = true;
                        // 3. process messages in queue and empty it
                        this.$store.state.intercomEventQueue.forEach((item: { id: string; data: IntercomEvent }) => {
                            const { data } = item;
                            if (!data) return;
                            this.processIntercomMessage(data);
                            // 4. remove item from queue
                            this.$store.commit('REMOVE_FROM_INTERCOM_QEUE', item);
                        });
                    } catch {
                        // Leave Intercom unbooted so a later ready/store change can retry.
                    }
                })();

                try {
                    await this.intercomBootPromise;
                } finally {
                    this.intercomBootPromise = null;
                }
            }
        },
        processIntercomMessage(data: IntercomEvent) {
            const { type, payload, eventName = 'defaultEvent' } = data;
            // 1. continue if user = admin
            if ((this.$store?.state?.me?.Permissions || '').includes('space_admin')) {
                // 2. check if intercom is ready
                if (this.intercomHasBooted) {
                    // 3. true: process message directly
                    if (type === 'update') {
                        this.$intercom.update(payload);
                    } else if (type === 'trackEvent') {
                        this.$intercom.trackEvent(eventName.toString(), payload);
                    }
                } else {
                    this.$store.commit('ADD_TO_INTERCOM_QEUE', data);
                }
            }
        },
        /*
    injectCustomerJavaScript

    Known issues:
    when user state changes from unknown to logged in without App.vue being newly mounted,
    placeholders will remain in unknown user state, result {{placeholders}} are not populated with data

    Description:
    injects customer defined JavaScript with placeholders in <head> with {{placeholders}} populated with user profile data, when available.
    When called in mounted this.$store.state.space and this.$store.state.me are available.
    */
        injectCustomerJavaScript() {
            if (this.$store.state.isInMobile) {
                return;
            }

            let javascript = this.$store.state?.space?.Javascript;
            const user = this.$store.state?.me;

            if (javascript) {
                if (user.Email) {
                    // map user status to
                    let userStatus = 'pending';

                    switch (user.Status) {
                        case 0:
                            userStatus = 'active';
                            break;
                        case 1:
                            userStatus = 'pending';
                            break;
                        case 2:
                            userStatus = 'deleted';
                            break;
                        case 3:
                            userStatus = 'suspended';
                            break;
                        case 4:
                            userStatus = 'cancelled';
                            break;
                        case 5:
                            userStatus = 'waiting_for_approval';
                            break;
                        case 6:
                            userStatus = 'rejected';
                            break;
                        case 7:
                            userStatus = 'guest';
                            break;
                        case 8:
                            userStatus = 'banned';
                            break;
                        default:
                            userStatus = 'active';
                            break;
                    }
                    javascript = javascript.replaceAll('{{name}}', `"${this.$store.state.me.Profile.Name}"`);
                    javascript = javascript.replaceAll('{{ name }}', `"${this.$store.state.me.Profile.Name}"`);
                    javascript = javascript.replaceAll('{{email}}', `"${this.$store.state.me.Email}"`);
                    javascript = javascript.replaceAll('{{ email }}', `"${this.$store.state.me.Email}"`);
                    javascript = javascript.replaceAll('{{user_id}}', `"${this.$store.state.me.ID}"`);
                    javascript = javascript.replaceAll('{{ user_id }}', `"${this.$store.state.me.ID}"`);
                    javascript = javascript.replaceAll('{{user_status}}', `"${userStatus}"`);
                    javascript = javascript.replaceAll('{{ user_status }}', `"${userStatus}"`);
                } else {
                    javascript = javascript.replaceAll(/\{\{\s{0,}[a-z0-9_-]+\s{0,}\}\}/gi, null);
                }

                console?.info('Custom JavaScript added to the DOM');
                // wrap he custom JavaScript in try catch statement to prevent it from breaking the DOM from loading
                javascript = `try {${javascript}} catch (err) { if (console && console.warn) {console.warn('There was an error in your custom JavaScript', err)}}`;

                // add the custom JavaScript to the DOM
                const dynamicScript = document.createElement('script');
                dynamicScript.setAttribute('type', 'text/javascript');
                dynamicScript.setAttribute('data-id', 'customerJavaScriptInjection');
                dynamicScript.innerHTML = javascript;
                document.head.appendChild(dynamicScript);
            }
        },
    },
};
